import React, { useEffect, useState } from 'react';

import Button from '~components/Button/Button';
import { getLocationByZip } from '~components/ZipCodeForm/util';
import { getUrlParam, addUrlParam } from '~globals/common';
import { fireTrackEvent, firePageViewEvent } from '~globals/tracking';
import { fireOpenModal } from '~globals/tracking';
import { useAppDispatch, useAppSelector } from '~hooks/useRedux';
import { updatePcCensus } from '~redux/Actions/DisplayActions';
import { updateFilter, updateZipAndCounty } from '~redux/Actions/GlobalActions';

import BirthYearInput from './inputs/BirthYearInput';
import PersonalCodeInput from './inputs/PersonalCodeInput';
import ZipInput from './inputs/ZipInput';

const ERR_TYPE = {
    code: {
        empty: 'PlanMatch code is required',
        invalid: 'Please enter a valid code',
    },
    year: {
        empty: 'Year of birth is required',
        tooShort: 'Please enter a four digit year of birth',
        future: 'Sorry, future years are not allowed',
        tooOld: 'Please enter a more recent year',
    },
    zip: {
        empty: 'ZIP code is required',
        invalid: 'Please enter a valid ZIP code',
    },
    county: {
        empty: 'Select your county',
    },
};

type Props = { redesign?: boolean };

const PersonalCodeForm = ({ redesign = false }: Props) => {
    const ehmpTarget = getUrlParam('target') || 'medicare-shopping';
    const planType = getUrlParam('type') || 'MA';
    const filter = getUrlParam('filter');
    let codeParam = getUrlParam('code')?.replace(/([^[a-zA-Z0-}9])/g, '') || '';
    if (codeParam.length < 6) {
        codeParam = '';
    }

    const dispatch = useAppDispatch();

    const allianceId = useAppSelector((state) => state.global.allianceId);
    const storeZip = useAppSelector((state) => state.global.geoLocation.zip);
    const storeCounty = useAppSelector((state) => state.global.geoLocation.countyName);

    const [codeValue, setCodeValue] = useState(codeParam || '');
    const [yearValue, setYearValue] = useState('');
    const [zipValue, setZipValue] = useState(storeZip);
    const [countyValue, setCountyValue] = useState(storeCounty);
    const [multiCountyList, setMultiCountyList] = useState(null as null | GeoLocation[]);

    const [codeError, setCodeError] = useState('');
    const [yearError, setYearError] = useState('');
    const [zipError, setZipError] = useState('');
    const [countyError, setCountyError] = useState('');

    // used to hide error evaluation before first attempt
    const [shouldDisplayErrors, setShouldDisplayErrors] = useState(false);

    const currentYear = new Date().getFullYear();

    const trackFormErrors = (error: string) => {
        fireTrackEvent({
            form: {
                errors: error,
            },
        });
    };

    useEffect(() => {
        if (codeError && codeValue.length === 6) {
            setCodeError('');
        }
    }, [codeValue]);

    useEffect(() => {
        if (yearError && yearValue.length === 4) {
            setYearError('');
        }
    }, [yearValue]);

    useEffect(() => {
        setCountyValue('');
        setMultiCountyList(null);

        if (zipValue.length === 5) {
            setZipError('');
            getLocationByZip(zipValue)
                .then((response) => {
                    if (!response || response.data.areaByZipCode.hasError) {
                        setZipError(ERR_TYPE.zip.invalid);
                        trackFormErrors('invalid zipcode');
                        return;
                    }
                    const { locationList } = response.data.areaByZipCode;
                    if (locationList.length === 1) {
                        setCountyValue(locationList[0].countyName);
                        setCountyError('');
                    }
                    if (locationList.length > 1) {
                        setMultiCountyList(locationList);
                        if (!shouldDisplayErrors) {
                            setCountyError(ERR_TYPE.county.empty);
                        } else {
                            setCountyError('');
                        }
                    }
                })
                .catch((e) => {
                    throw e;
                });
        }
    }, [zipValue]);

    useEffect(() => {
        if (countyError && countyValue) {
            setCountyError('');
        }
    }, [countyValue]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (validateForm()) {
            // store validated zip
            dispatch(updateZipAndCounty(zipValue, countyValue));

            // handle api call
            handlePersonalCodeRedirect();
        } else if (!shouldDisplayErrors) {
            setShouldDisplayErrors(true);
        }
    };

    const validateForm = () => {
        let isValid = true;

        if (!_validateCode()) {
            isValid = false;
        }
        if (!_validateYear()) {
            isValid = false;
        }
        if (!_validateZip()) {
            isValid = false;
        }
        if (!countyValue) {
            setCountyError(ERR_TYPE.county.empty);
            isValid = false;
        }
        if (codeError || yearError || zipError || countyError) {
            isValid = false;
        }
        return isValid;
    };

    const _validateCode = () => {
        if (!codeValue) {
            setCodeError(ERR_TYPE.code.empty);
            return false;
        }
        if (codeValue.length < 6) {
            setCodeError(ERR_TYPE.code.invalid);
            trackFormErrors('invalid planmatch code');
            return false;
        }
        return true;
    };

    const _validateYear = () => {
        if (!yearValue) {
            setYearError(ERR_TYPE.year.empty);
            return false;
        }
        if (yearValue.length < 4) {
            setYearError(ERR_TYPE.year.tooShort);
            trackFormErrors('invalid four digit birth year');
            return false;
        }
        if (Number(yearValue) > currentYear) {
            setYearError(ERR_TYPE.year.future);
            return false;
        }
        if (currentYear - Number(yearValue) > 120) {
            setYearError(ERR_TYPE.year.tooOld);
            return false;
        }
        return true;
    };

    const _validateZip = () => {
        if (!zipValue) {
            setZipError(ERR_TYPE.zip.empty);
            trackFormErrors('zipcode required');
            return false;
        }
        if (zipValue.length < 5) {
            setZipError(ERR_TYPE.zip.invalid);
            trackFormErrors('invalid zipcode');
            return false;
        }
        return true;
    };

    const handlePersonalCodeRedirect = () => {
        const variables = {
            uCode: codeValue,
            allianceId: allianceId,
            yearOfBirth: yearValue,
            zip: zipValue,
            county: countyValue,
            target: ehmpTarget,
            planType: planType,
        };

        const endpoint = `${process.env.GATSBY_EHMP_GQL_URL}api/partner/verifyUCode`;

        fetch(endpoint, {
            headers: {
                accept: '/',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(variables),
            method: 'POST',
        })
            .then((res) => res.json())
            .then((data: { redirectURL: string; hasError: boolean; errorCode: string }) => {
                const { redirectURL, hasError, errorCode } = data;
                if (!hasError) {
                    let redirect = addUrlParam(redirectURL, 'uiVersion', redesign);
                    if (filter && filter.length > 0) {
                        redirect = addUrlParam(redirect, 'filter', filter);
                    }

                    if (typeof window._satellite !== 'undefined') {
                        window._satellite.getVar('Cross Domain Parameters')?.forEach((param) => {
                            redirect = addUrlParam(redirect, param.key, param.value);
                        });
                    }

                    // fire tracking event
                    fireTrackEvent({
                        page: {
                            action:
                                process.env.GATSBY_ANALYTICS_SITE_GROUP + ':lead form:get started',
                        },
                        leadInfo: {
                            zip: zipValue,
                            county: countyValue,
                            age: new Date().getFullYear() - parseInt(yearValue),
                            personalCode: codeValue,
                        },
                    });

                    // redirect to EHMP
                    if (typeof window !== 'undefined') {
                        window.location.href = redirect;
                    }
                } else {
                    showErrorModal(errorCode, redirectURL);
                }
            })
            .catch((err) => {
                console.error('Error: ', err);
            });
    };

    const showErrorModal = (errorCode: string, redirectURL: string) => {
        let errorType, title, message;
        switch (errorCode) {
            case 'UC0002': // too many requests
                errorType = 'maxAttempts';
                title = "You've exceeded your allowed entry attempts";
                message = 'For security purposes, you may now browse via ZIP Code only.';
                break;
            case '10008': // no data returned
                errorType = 'profileEmpty';
                title = 'See Medicare Plans Based on Your PlanMatch Code';
                message = `You're seeing plans in your area based on your ZIP Code. To see plans that include your doctors, RX drugs, and pharmacy, add them with our search tools on the next screen.`;
                break;
            case 'UC0001':
            default:
                errorType = 'generic';
                title = `Mmmm…we can't find your code`;
                message = 'Try re-entering your information.';
                break;
        }

        dispatch(
            updatePcCensus({
                errorType: errorType,
                modalInfo: {
                    title: title,
                    message: message,
                    redirectURL,
                },
                modalOpen: true,
                redirectParams: {
                    target: ehmpTarget,
                    type: planType,
                },
            })
        );

        if (filter && filter.length > 0) {
            dispatch(updateFilter(filter));
        }

        // fire error page tracking
        fireOpenModal();
        firePageViewEvent({
            page: {
                name:
                    errorType === 'profileEmpty' ? 'see plans based on planMatch code' : errorType,
                section: errorType === 'profileEmpty' ? 'planMatch' : 'error page',
            },
        });
    };

    return (
        <>
            {!redesign ? (
                <form className="flex flex-col md:flex-row mb-4">
                    <PersonalCodeInput
                        shouldDisplayErrors={!!codeError && shouldDisplayErrors}
                        errorValue={codeError}
                        codeValue={codeValue}
                        setCodeValue={setCodeValue}
                    />
                    <BirthYearInput
                        shouldDisplayErrors={!!yearError && shouldDisplayErrors}
                        errorValue={yearError}
                        yearValue={yearValue}
                        setYearValue={setYearValue}
                    />
                    <ZipInput
                        shouldDisplayErrors={!!(zipError || countyError) && shouldDisplayErrors}
                        errorValue={{ zip: zipError, county: countyError }}
                        zipValue={zipValue}
                        countyValue={countyValue}
                        setZipValue={setZipValue}
                        setCountyValue={setCountyValue}
                        multiCountyList={multiCountyList}
                    />
                    <Button
                        className="whitespace-nowrap w-full md:w-auto md:mb-2 md:mt-6 md:max-h-[50px]"
                        data-qa="submit"
                        type="submit"
                        onClick={handleSubmit}
                        data-wa-link="See Medicare Plans"
                    >
                        See Medicare Plans
                    </Button>
                </form>
            ) : (
                <form className="flex flex-col xl:w-full xl:flex-row">
                    <div className="flex md:mb-4 xl:mr-4 xl:mb-0">
                        <PersonalCodeInput
                            shouldDisplayErrors={!!codeError && shouldDisplayErrors}
                            errorValue={codeError}
                            codeValue={codeValue}
                            setCodeValue={setCodeValue}
                            redesign={redesign}
                        />
                        <BirthYearInput
                            shouldDisplayErrors={!!yearError && shouldDisplayErrors}
                            errorValue={yearError}
                            yearValue={yearValue}
                            setYearValue={setYearValue}
                            redesign={redesign}
                        />
                    </div>
                    <ZipInput
                        shouldDisplayErrors={!!(zipError || countyError) && shouldDisplayErrors}
                        errorValue={{ zip: zipError, county: countyError }}
                        zipValue={zipValue}
                        countyValue={countyValue}
                        setZipValue={setZipValue}
                        setCountyValue={setCountyValue}
                        multiCountyList={multiCountyList}
                        redesign={redesign}
                    />
                    <Button
                        className="whitespace-nowrap w-full lg:w-auto md:mb-2 md:mt-6 md:max-h-[50px]"
                        data-qa="submit"
                        type="submit"
                        styletype="orange"
                        onClick={handleSubmit}
                        data-wa-link="See Medicare Plans"
                    >
                        See Medicare Plans
                    </Button>
                </form>
            )}
        </>
    );
};

export default PersonalCodeForm;
